/**
 * @fileoverview Filter Module Type Definitions
 * 
 * This file contains all the JSDoc type definitions and validation functions
 * for the Filter Module system.
 * 
 * Requirements: 3.1, 3.2, 10.1, 10.2
 */

/**
 * @typedef {Object} FilterConfig
 * @property {'knowledge-base'|'admin-users'|'admin-parrots'|'user-dashboard'|'conversations'|'admin-audit'} context - Context where the filter is used
 * @property {FilterDefinition[]} filters - Array of filter definitions
 * @property {'horizontal'|'vertical'|'dropdown'} layout - Layout style for the filter interface
 * @property {function(ActiveFilter[]): void} [onFilterChange] - Callback when filters change
 * @property {function(): void} [onFilterReset] - Callback when filters are reset
 * @property {boolean} [persistFilters] - Whether to persist filters in storage
 * @property {string} [storageKey] - Key for storing filter state
 * @property {boolean} [showClearAll] - Whether to show clear all button
 * @property {boolean} [showExport] - Whether to show export functionality
 * @property {boolean} [showImport] - Whether to show import functionality
 * @property {boolean} [realTimeFiltering] - Whether to apply filters in real-time
 * @property {number} [debounceMs] - Debounce delay for real-time filtering
 * @property {FilterPreset[]} [presets] - Predefined filter presets
 */

/**
 * @typedef {Object} FilterDefinition
 * @property {string} id - Unique identifier for the filter
 * @property {'text'|'select'|'date-range'|'number-range'|'boolean'} type - Type of filter input
 * @property {string} label - Display label for the filter
 * @property {string} [placeholder] - Placeholder text for input fields
 * @property {FilterOption[]} [options] - Options for select-type filters
 * @property {ValidationRule[]} [validation] - Validation rules for the filter
 * @property {*} [defaultValue] - Default value for the filter
 * @property {boolean} [required] - Whether the filter is required
 * @property {boolean} [disabled] - Whether the filter is disabled
 * @property {string} [description] - Help text for the filter
 * @property {FilterOperator[]} [operators] - Available operators for this filter
 * @property {Object} [config] - Additional configuration specific to filter type
 */

/**
 * @typedef {Object} FilterOption
 * @property {string|number|boolean} value - Option value
 * @property {string} label - Display label for the option
 * @property {boolean} [disabled] - Whether the option is disabled
 * @property {string} [description] - Optional description for the option
 * @property {Object} [metadata] - Additional metadata for the option
 */

/**
 * @typedef {Object} ValidationRule
 * @property {'required'|'minLength'|'maxLength'|'min'|'max'|'pattern'|'custom'} type - Validation type
 * @property {*} [value] - Validation value (e.g., min length, pattern)
 * @property {string} message - Error message for validation failure
 * @property {function(*): boolean} [validator] - Custom validation function
 */

/**
 * @typedef {Object} ActiveFilter
 * @property {string} id - Filter ID
 * @property {*} value - Current filter value
 * @property {FilterOperator} operator - Filter operator
 * @property {boolean} [enabled] - Whether the filter is currently active
 * @property {Date} [appliedAt] - When the filter was applied
 */

/**
 * @typedef {Object} FilterOperator
 * @property {'equals'|'contains'|'starts-with'|'ends-with'|'greater-than'|'less-than'|'between'|'in'|'not-in'} type - Operator type
 * @property {string} label - Display label for the operator
 * @property {string} [symbol] - Symbol representation of the operator
 * @property {boolean} [requiresValue] - Whether the operator requires a value
 * @property {boolean} [requiresArray] - Whether the operator requires an array value
 */

/**
 * @typedef {Object} FilterPreset
 * @property {string} id - Preset ID
 * @property {string} name - Preset name
 * @property {string} [description] - Preset description
 * @property {ActiveFilter[]} filters - Filters in this preset
 * @property {boolean} [isDefault] - Whether this is the default preset
 * @property {Date} [createdAt] - When the preset was created
 * @property {Date} [updatedAt] - When the preset was last updated
 */

/**
 * @typedef {Object} FilterState
 * @property {ActiveFilter[]} activeFilters - Currently active filters
 * @property {FilterPreset[]} presets - Available filter presets
 * @property {string} [activePreset] - Currently active preset ID
 * @property {boolean} loading - Whether filters are being applied
 * @property {string} [error] - Current error state
 * @property {Date} lastUpdated - When filters were last updated
 * @property {Object} [cache] - Cached filter results
 */

/**
 * @typedef {Object} FilterResult
 * @property {*[]} data - Filtered data
 * @property {number} totalCount - Total number of items before filtering
 * @property {number} filteredCount - Number of items after filtering
 * @property {ActiveFilter[]} appliedFilters - Filters that were applied
 * @property {Date} timestamp - When the filtering was performed
 * @property {Object} [metadata] - Additional result metadata
 */

/**
 * Default filter operators by type
 */
export const DEFAULT_OPERATORS = {
  text: [
    { type: 'contains', label: 'Contains', symbol: '~', requiresValue: true },
    { type: 'equals', label: 'Equals', symbol: '=', requiresValue: true },
    { type: 'starts-with', label: 'Starts with', symbol: '^', requiresValue: true },
    { type: 'ends-with', label: 'Ends with', symbol: '$', requiresValue: true }
  ],
  select: [
    { type: 'equals', label: 'Is', symbol: '=', requiresValue: true },
    { type: 'in', label: 'Is one of', symbol: '∈', requiresValue: true, requiresArray: true },
    { type: 'not-in', label: 'Is not one of', symbol: '∉', requiresValue: true, requiresArray: true }
  ],
  'number-range': [
    { type: 'equals', label: 'Equals', symbol: '=', requiresValue: true },
    { type: 'greater-than', label: 'Greater than', symbol: '>', requiresValue: true },
    { type: 'less-than', label: 'Less than', symbol: '<', requiresValue: true },
    { type: 'between', label: 'Between', symbol: '≤≥', requiresValue: true, requiresArray: true }
  ],
  'date-range': [
    { type: 'equals', label: 'On', symbol: '=', requiresValue: true },
    { type: 'greater-than', label: 'After', symbol: '>', requiresValue: true },
    { type: 'less-than', label: 'Before', symbol: '<', requiresValue: true },
    { type: 'between', label: 'Between', symbol: '≤≥', requiresValue: true, requiresArray: true }
  ],
  boolean: [
    { type: 'equals', label: 'Is', symbol: '=', requiresValue: true }
  ]
};

/**
 * Context-specific filter definitions
 */
export const CONTEXT_FILTER_DEFINITIONS = {
  'knowledge-base': [
    {
      id: 'name',
      type: 'text',
      label: 'File Name',
      placeholder: 'Search by file name...',
      operators: DEFAULT_OPERATORS.text
    },
    {
      id: 'file_type',
      type: 'select',
      label: 'File Type',
      options: [
        { value: 'pdf', label: 'PDF' },
        { value: 'docx', label: 'DOCX' },
        { value: 'txt', label: 'TXT' },
        { value: 'html', label: 'HTML' }
      ],
      operators: DEFAULT_OPERATORS.select
    },
    {
      id: 'sync_status',
      type: 'select',
      label: 'Sync Status',
      options: [
        { value: 'not_assigned', label: 'Not Assigned' },
        { value: 'pending', label: 'Pending' },
        { value: 'syncing', label: 'Syncing' },
        { value: 'synced', label: 'Synced' },
        { value: 'failed', label: 'Failed' }
      ],
      operators: DEFAULT_OPERATORS.select
    },
    {
      id: 'type',
      type: 'select',
      label: 'Content Type',
      options: [
        { value: 'file', label: 'File Upload' },
        { value: 'url', label: 'URL' },
        { value: 'text', label: 'Text Content' }
      ],
      operators: DEFAULT_OPERATORS.select
    },
    {
      id: 'size',
      type: 'number-range',
      label: 'File Size (MB)',
      operators: DEFAULT_OPERATORS['number-range'],
      config: {
        min: 0,
        max: 4,
        step: 0.1
      }
    },
    {
      id: 'created_at',
      type: 'date-range',
      label: 'Created Date',
      operators: DEFAULT_OPERATORS['date-range']
    },
    {
      id: 'updated_at',
      type: 'date-range',
      label: 'Modified Date',
      operators: DEFAULT_OPERATORS['date-range']
    },
    {
      id: 'last_sync_attempt',
      type: 'date-range',
      label: 'Last Sync Attempt',
      operators: DEFAULT_OPERATORS['date-range']
    }
  ],
  'admin-users': [
    {
      id: 'name',
      type: 'text',
      label: 'Name',
      placeholder: 'Search by name...',
      operators: DEFAULT_OPERATORS.text
    },
    {
      id: 'email',
      type: 'text',
      label: 'Email',
      placeholder: 'Search by email...',
      operators: DEFAULT_OPERATORS.text
    },
    {
      id: 'subscription',
      type: 'select',
      label: 'Subscription',
      options: [
        { value: 'free', label: 'Free' },
        { value: 'basic', label: 'Basic' },
        { value: 'pro', label: 'Pro' },
        { value: 'enterprise', label: 'Enterprise' }
      ],
      operators: DEFAULT_OPERATORS.select
    },
    {
      id: 'status',
      type: 'select',
      label: 'Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'suspended', label: 'Suspended' }
      ],
      operators: DEFAULT_OPERATORS.select
    },
    {
      id: 'created_at',
      type: 'date-range',
      label: 'Created Date',
      operators: DEFAULT_OPERATORS['date-range']
    },
    {
      id: 'last_login',
      type: 'date-range',
      label: 'Last Login',
      operators: DEFAULT_OPERATORS['date-range']
    }
  ],
  'admin-parrots': [
    {
      id: 'name',
      type: 'text',
      label: 'Parrot Name',
      placeholder: 'Search by parrot name...',
      operators: DEFAULT_OPERATORS.text
    },
    {
      id: 'status',
      type: 'select',
      label: 'Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'training', label: 'Training' },
        { value: 'error', label: 'Error' }
      ],
      operators: DEFAULT_OPERATORS.select
    },
    {
      id: 'voice_model',
      type: 'select',
      label: 'Voice Model',
      options: [
        { value: 'default', label: 'Default' },
        { value: 'custom', label: 'Custom' },
        { value: 'premium', label: 'Premium' }
      ],
      operators: DEFAULT_OPERATORS.select
    },
    {
      id: 'knowledge_base_count',
      type: 'number-range',
      label: 'Knowledge Base Files',
      operators: DEFAULT_OPERATORS['number-range']
    },
    {
      id: 'owner_email',
      type: 'text',
      label: 'Owner Email',
      placeholder: 'Search by owner email...',
      operators: DEFAULT_OPERATORS.text
    },
    {
      id: 'created_at',
      type: 'date-range',
      label: 'Created Date',
      operators: DEFAULT_OPERATORS['date-range']
    },
    {
      id: 'last_used',
      type: 'date-range',
      label: 'Last Used',
      operators: DEFAULT_OPERATORS['date-range']
    }
  ],
  'user-dashboard': [
    {
      id: 'name',
      type: 'text',
      label: 'Name',
      placeholder: 'Search by name...',
      operators: DEFAULT_OPERATORS.text
    },
    {
      id: 'status',
      type: 'select',
      label: 'Status',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ],
      operators: DEFAULT_OPERATORS.select
    },
    {
      id: 'created_at',
      type: 'date-range',
      label: 'Created Date',
      operators: DEFAULT_OPERATORS['date-range']
    }
  ],
  'conversations': [
    {
      id: 'phone_number',
      type: 'text',
      label: 'Phone Number',
      placeholder: 'Search by phone number...',
      operators: DEFAULT_OPERATORS.text
    },
    {
      id: 'parrot_name',
      type: 'text',
      label: 'Parrot Name',
      placeholder: 'Search by parrot name...',
      operators: DEFAULT_OPERATORS.text
    },
    {
      id: 'duration',
      type: 'number-range',
      label: 'Duration (seconds)',
      operators: DEFAULT_OPERATORS['number-range']
    },
    {
      id: 'status',
      type: 'select',
      label: 'Call Status',
      options: [
        { value: 'completed', label: 'Completed' },
        { value: 'failed', label: 'Failed' },
        { value: 'busy', label: 'Busy' },
        { value: 'no-answer', label: 'No Answer' }
      ],
      operators: DEFAULT_OPERATORS.select
    },
    {
      id: 'date',
      type: 'date-range',
      label: 'Call Date',
      operators: DEFAULT_OPERATORS['date-range']
    }
  ],
  'admin-audit': [
    {
      id: 'search',
      type: 'text',
      label: 'Search',
      placeholder: 'Search logs...',
      operators: DEFAULT_OPERATORS.text
    },
    {
      id: 'operation_type',
      type: 'select',
      label: 'Operation',
      options: [
        { value: 'CREATE', label: 'Create' },
        { value: 'UPDATE', label: 'Update' },
        { value: 'DELETE', label: 'Delete' },
        { value: 'VIEW', label: 'View' },
        { value: 'LOGIN', label: 'Login' },
        { value: 'LOGOUT', label: 'Logout' },
        { value: 'EXPORT', label: 'Export' },
        { value: 'IMPORT', label: 'Import' }
      ],
      operators: DEFAULT_OPERATORS.select
    },
    {
      id: 'resource_type',
      type: 'select',
      label: 'Resource',
      options: [
        { value: 'USER', label: 'User' },
        { value: 'PARROT', label: 'Parrot' },
        { value: 'PHONE_NUMBER', label: 'Phone Number' },
        { value: 'RAG_FILE', label: 'RAG File' },
        { value: 'SUBSCRIPTION', label: 'Subscription' },
        { value: 'ADMIN', label: 'Admin' },
        { value: 'SYSTEM', label: 'System' }
      ],
      operators: DEFAULT_OPERATORS.select
    },
    {
      id: 'status',
      type: 'select',
      label: 'Status',
      options: [
        { value: 'success', label: 'Success' },
        { value: 'failure', label: 'Failed' },
        { value: 'pending', label: 'Pending' }
      ],
      operators: DEFAULT_OPERATORS.select
    },
    {
      id: 'user_id',
      type: 'text',
      label: 'User ID',
      placeholder: 'Enter user ID...',
      operators: DEFAULT_OPERATORS.text
    },
    {
      id: 'admin_id',
      type: 'text',
      label: 'Admin ID',
      placeholder: 'Enter admin ID...',
      operators: DEFAULT_OPERATORS.text
    },
    {
      id: 'date_range',
      type: 'date-range',
      label: 'Date Range',
      operators: DEFAULT_OPERATORS['date-range']
    }
  ]
};

/**
 * Validate FilterConfig object
 * @param {FilterConfig} config - Configuration to validate
 * @returns {{valid: boolean, errors: string[]}} Validation result
 */
export function validateFilterConfig(config) {
  const errors = [];

  if (!config || typeof config !== 'object') {
    errors.push('Config must be an object');
    return { valid: false, errors };
  }

  // Validate required fields
  if (!config.context) {
    errors.push('Context is required');
  } else if (!['knowledge-base', 'admin-users', 'admin-parrots', 'user-dashboard', 'conversations', 'admin-audit'].includes(config.context)) {
    errors.push('Context must be one of: knowledge-base, admin-users, admin-parrots, user-dashboard, conversations, admin-audit');
  }

  if (!config.filters || !Array.isArray(config.filters)) {
    errors.push('Filters must be an array');
  } else {
    // Validate each filter definition
    config.filters.forEach((filter, index) => {
      const filterErrors = validateFilterDefinition(filter);
      if (!filterErrors.valid) {
        errors.push(`Filter ${index}: ${filterErrors.errors.join(', ')}`);
      }
    });
  }

  if (config.layout && !['horizontal', 'vertical', 'dropdown'].includes(config.layout)) {
    errors.push('Layout must be one of: horizontal, vertical, dropdown');
  }

  // Validate optional callback functions
  if (config.onFilterChange && typeof config.onFilterChange !== 'function') {
    errors.push('onFilterChange must be a function');
  }

  if (config.onFilterReset && typeof config.onFilterReset !== 'function') {
    errors.push('onFilterReset must be a function');
  }

  // Validate storage configuration
  if (config.persistFilters && !config.storageKey) {
    errors.push('storageKey is required when persistFilters is true');
  }

  // Validate debounce configuration
  if (config.debounceMs && (typeof config.debounceMs !== 'number' || config.debounceMs < 0)) {
    errors.push('debounceMs must be a positive number');
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate FilterDefinition object
 * @param {FilterDefinition} filter - Filter definition to validate
 * @returns {{valid: boolean, errors: string[]}} Validation result
 */
export function validateFilterDefinition(filter) {
  const errors = [];

  if (!filter || typeof filter !== 'object') {
    errors.push('Filter must be an object');
    return { valid: false, errors };
  }

  // Validate required fields
  if (!filter.id) {
    errors.push('Filter ID is required');
  }

  if (!filter.type) {
    errors.push('Filter type is required');
  } else if (!['text', 'select', 'date-range', 'number-range', 'boolean'].includes(filter.type)) {
    errors.push('Filter type must be one of: text, select, date-range, number-range, boolean');
  }

  if (!filter.label) {
    errors.push('Filter label is required');
  }

  // Validate type-specific requirements
  if (filter.type === 'select' && (!filter.options || !Array.isArray(filter.options))) {
    errors.push('Select filters must have options array');
  }

  // Validate options if present
  if (filter.options) {
    filter.options.forEach((option, index) => {
      if (!option || typeof option !== 'object') {
        errors.push(`Option ${index} must be an object`);
      } else {
        if (option.value === undefined || option.value === null) {
          errors.push(`Option ${index} must have a value`);
        }
        if (!option.label) {
          errors.push(`Option ${index} must have a label`);
        }
      }
    });
  }

  // Validate validation rules if present
  if (filter.validation) {
    if (!Array.isArray(filter.validation)) {
      errors.push('Validation must be an array');
    } else {
      filter.validation.forEach((rule, index) => {
        if (!rule.type) {
          errors.push(`Validation rule ${index} must have a type`);
        }
        if (!rule.message) {
          errors.push(`Validation rule ${index} must have a message`);
        }
      });
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Default filter presets by context
 */
export const DEFAULT_FILTER_PRESETS = {
  'knowledge-base': [
    {
      id: 'synced-files',
      name: 'Synced Files',
      description: 'Show only files that have been successfully synced',
      filters: [
        {
          id: 'sync_status',
          value: 'synced',
          operator: { type: 'equals', label: 'Is', symbol: '=', requiresValue: true },
          enabled: true,
          appliedAt: new Date()
        }
      ],
      isDefault: false,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 'failed-syncs',
      name: 'Failed Syncs',
      description: 'Show files that failed to sync',
      filters: [
        {
          id: 'sync_status',
          value: 'failed',
          operator: { type: 'equals', label: 'Is', symbol: '=', requiresValue: true },
          enabled: true,
          appliedAt: new Date()
        }
      ],
      isDefault: false,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 'pdf-files',
      name: 'PDF Files',
      description: 'Show only PDF files',
      filters: [
        {
          id: 'file_type',
          value: 'pdf',
          operator: { type: 'equals', label: 'Is', symbol: '=', requiresValue: true },
          enabled: true,
          appliedAt: new Date()
        }
      ],
      isDefault: false,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 'recent-files',
      name: 'Recent Files',
      description: 'Show files created in the last 7 days',
      filters: [
        {
          id: 'created_at',
          value: [
            new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            new Date().toISOString().split('T')[0]
          ],
          operator: { type: 'between', label: 'Between', symbol: '≤≥', requiresValue: true, requiresArray: true },
          enabled: true,
          appliedAt: new Date()
        }
      ],
      isDefault: false,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 'large-files',
      name: 'Large Files',
      description: 'Show files larger than 2MB',
      filters: [
        {
          id: 'size',
          value: 2,
          operator: { type: 'greater-than', label: 'Greater than', symbol: '>', requiresValue: true },
          enabled: true,
          appliedAt: new Date()
        }
      ],
      isDefault: false,
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ]
};

/**
 * Get default configuration for FilterModule
 * @param {string} context - Filter context
 * @returns {FilterConfig} Default configuration
 */
export function getDefaultFilterConfig(context) {
  const contextFilters = CONTEXT_FILTER_DEFINITIONS[context] || [];
  const contextPresets = DEFAULT_FILTER_PRESETS[context] || [];
  
  return {
    context,
    filters: contextFilters,
    layout: 'horizontal',
    persistFilters: true,
    storageKey: `filter-${context}`,
    showClearAll: true,
    showExport: context === 'knowledge-base', // Enable export for knowledge base
    showImport: context === 'knowledge-base', // Enable import for knowledge base
    realTimeFiltering: true,
    debounceMs: 300,
    presets: contextPresets
  };
}

/**
 * Create an active filter from a filter definition and value
 * @param {FilterDefinition} definition - Filter definition
 * @param {*} value - Filter value
 * @param {FilterOperator} [operator] - Filter operator (defaults to first available)
 * @returns {ActiveFilter} Active filter object
 */
export function createActiveFilter(definition, value, operator = null) {
  const availableOperators = definition.operators || DEFAULT_OPERATORS[definition.type] || [];
  const selectedOperator = operator || availableOperators[0];

  return {
    id: definition.id,
    value,
    operator: selectedOperator,
    enabled: true,
    appliedAt: new Date()
  };
}

/**
 * Apply filters to a dataset
 * @param {*[]} data - Data to filter
 * @param {ActiveFilter[]} filters - Filters to apply
 * @returns {FilterResult} Filter result
 */
export function applyFilters(data, filters) {
  const startTime = Date.now();
  const activeFilters = filters.filter(f => f.enabled && f.value !== null && f.value !== undefined && f.value !== '');
  
  if (activeFilters.length === 0) {
    return {
      data,
      totalCount: data.length,
      filteredCount: data.length,
      appliedFilters: [],
      timestamp: new Date()
    };
  }

  const filteredData = data.filter(item => {
    return activeFilters.every(filter => {
      const itemValue = getNestedValue(item, filter.id);
      return applyFilterOperator(itemValue, filter.value, filter.operator);
    });
  });

  return {
    data: filteredData,
    totalCount: data.length,
    filteredCount: filteredData.length,
    appliedFilters: activeFilters,
    timestamp: new Date(),
    metadata: {
      processingTime: Date.now() - startTime
    }
  };
}

/**
 * Get nested value from object using dot notation
 * @param {Object} obj - Object to get value from
 * @param {string} path - Dot notation path
 * @returns {*} Value at path
 */
function getNestedValue(obj, path) {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
}

/**
 * Apply a filter operator to compare values
 * @param {*} itemValue - Value from the data item
 * @param {*} filterValue - Filter value to compare against
 * @param {FilterOperator} operator - Operator to use for comparison
 * @returns {boolean} Whether the item matches the filter
 */
function applyFilterOperator(itemValue, filterValue, operator) {
  if (itemValue === null || itemValue === undefined) {
    return false;
  }

  const itemStr = String(itemValue).toLowerCase();
  const filterStr = String(filterValue).toLowerCase();

  switch (operator.type) {
    case 'equals':
      // Handle date comparisons
      if (typeof itemValue === 'string' && typeof filterValue === 'string' && 
          (itemValue.match(/^\d{4}-\d{2}-\d{2}/) || filterValue.match(/^\d{4}-\d{2}-\d{2}/))) {
        return new Date(itemValue).getTime() === new Date(filterValue).getTime();
      }
      return itemValue === filterValue;
    
    case 'contains':
      return itemStr.includes(filterStr);
    
    case 'starts-with':
      return itemStr.startsWith(filterStr);
    
    case 'ends-with':
      return itemStr.endsWith(filterStr);
    
    case 'greater-than':
      // Handle date comparisons
      if (typeof itemValue === 'string' && typeof filterValue === 'string' && 
          (itemValue.match(/^\d{4}-\d{2}-\d{2}/) || filterValue.match(/^\d{4}-\d{2}-\d{2}/))) {
        return new Date(itemValue) > new Date(filterValue);
      }
      return Number(itemValue) > Number(filterValue);
    
    case 'less-than':
      // Handle date comparisons
      if (typeof itemValue === 'string' && typeof filterValue === 'string' && 
          (itemValue.match(/^\d{4}-\d{2}-\d{2}/) || filterValue.match(/^\d{4}-\d{2}-\d{2}/))) {
        return new Date(itemValue) < new Date(filterValue);
      }
      return Number(itemValue) < Number(filterValue);
    
    case 'between':
      if (Array.isArray(filterValue) && filterValue.length === 2) {
        const numValue = Number(itemValue);
        return numValue >= Number(filterValue[0]) && numValue <= Number(filterValue[1]);
      }
      return false;
    
    case 'in':
      return Array.isArray(filterValue) && filterValue.includes(itemValue);
    
    case 'not-in':
      return Array.isArray(filterValue) && !filterValue.includes(itemValue);
    
    default:
      return true;
  }
}

// Export the configuration class for JavaScript usage
export class FilterConfig {
  constructor(config = {}) {
    const defaults = getDefaultFilterConfig(config.context || 'user-dashboard');
    Object.assign(this, { ...defaults, ...config });
  }
  
  validate() {
    return validateFilterConfig(this);
  }
}
